import { DeckConfig } from "../components/DeckCreator/DeckCreator";
import { StyleConfig } from "../components/DeckCreator/DeckCreator";
import { DeckPlan, stripDeckPlanTransientContext } from "../services/ai/deckPlannerService";
import { FinsterTask } from "../finsterApi";
import { streamSlideThumbnails } from "./slideService";
import { isOfficeJsBypassed } from "./officeJsGuard";
import { shouldUseFactSetBridge } from "./presentationHost";
import {
  areBrandingFeatureFlagsReady,
  getActiveBrandTemplateId,
  getFeatureFlagsInitStatus,
} from "./featureFlagService";
import {
  applyDialogPopupWindowGeometry,
  buildCenteredDialogPopupWindow,
  focusDialogPopupWindow,
} from "./dialogPopupWindow";
import { explainBrandResolution } from "../utils/auth";
import { logBrandingFlagDiag } from "../../shared/featureFlags/brandingFlagDiag";

/** Resolve the brand id (if any) for the dialog window from auth + flags. */
function resolveDialogBrandId(options: DeckCreatorDialogOptions): string {
  if (!areBrandingFeatureFlagsReady()) {
    logBrandingFlagDiag("dialogBrandResolutionSkipped", {
      companyId: options.userCompanyId || null,
      initStatus: getFeatureFlagsInitStatus(),
      reason: "brandingFlagsNotReady",
    });
    return "";
  }

  const resolution = explainBrandResolution({
    companyId: options.userCompanyId,
    activeBrandTemplateId: getActiveBrandTemplateId(),
  });
  const brandId = (resolution.resolvedBrandId as string | null) ?? "";
  return brandId;
}

export interface ExistingSlideInfo {
  index: number;
  slideNumber?: number;
  shapeCount?: number;
  thumbnail: string;
}

export interface DeckCreatorDialogResult {
  cancelled?: boolean;
  config?: DeckConfig;
  plan?: DeckPlan;
  error?: string;
}

export interface DeckCreatorDialogOptions {
  tasks: FinsterTask[];
  userEmail: string;
  userCompanyId?: string;
  /** When set, the dialog opens already on Review with this plan (no config form). */
  initialPlan?: DeckPlan;
  /** Live outline edits from the Review UI (does not close the dialog). */
  onPlanPatch?: (plan: DeckPlan) => void;
  /**
   * Fires when the Review window is actually showing (popup assigned / Office
   * dialog created). Does not wait for the user to confirm or cancel.
   */
  onOpened?: () => void;
  /**
   * Keeps the Review session open while an in-flight plan update, Skill
   * resolution, or evidence commit settles. The host re-reads its latest plan
   * after this barrier rather than trusting the pre-wait result snapshot.
   */
  beforeConfirm?: () => Promise<void>;
}

const DIALOG_DATA_KEY = "__finster_deck_dialog_data__";
const DIALOG_SLIDES_KEY = "__finster_deck_dialog_slides__";
const DIALOG_POST_MESSAGE_KIND = "finster.deckCreator.result";
const DIALOG_COMMAND_MESSAGE_KIND = "finster.deckCreator.command";
const DIALOG_PLAN_PATCH_KIND = "finster.deckCreator.planPatch";
const DIALOG_RESULT_STORAGE_KEY = "__finster_deck_dialog_result__";
const CURRENT_PRESENTATION_PREVIEW_HEIGHT = 720;

function buildPlaceholderSlides(totalSlides: number): ExistingSlideInfo[] {
  return Array.from({ length: Math.max(0, totalSlides) }, (_, index) => ({
    index,
    slideNumber: index + 1,
    thumbnail: "",
  }));
}

type DeckCreatorCommandPayload = { type: "close" };
type SlideStreamCommandPayload = {
  type: "slideStreamUpdate";
  streamId: number;
  slides?: ExistingSlideInfo[];
  slide?: ExistingSlideInfo;
  done: boolean;
  error?: boolean;
  totalSlides?: number;
};
type SlideStreamSyncRequestPayload = {
  type: "requestSlideStreamSync";
  streamId?: number;
};
type PlanUpdateCommandPayload = {
  type: "planUpdate";
  plan: DeckPlan;
};
type DeckCreatorDialogCommandPayload =
  | DeckCreatorCommandPayload
  | SlideStreamCommandPayload
  | SlideStreamSyncRequestPayload
  | PlanUpdateCommandPayload;

interface DeckCreatorCommandEnvelope {
  kind: typeof DIALOG_COMMAND_MESSAGE_KIND;
  payload: DeckCreatorDialogCommandPayload;
}

function stageDialogData(
  options: DeckCreatorDialogOptions,
  existingSlideInfo: ExistingSlideInfo[],
  slideStreamId: number
): void {
  const tasksSummary = options.tasks
    .filter(t => t.taskRuns && t.taskRuns.length > 0)
    .map(t => ({
      id: t.taskId,
      name: t.taskName,
      runs: (t.taskRuns || []).map(r => ({
        id: r.taskRunId,
        name: r.taskRunName,
        timestamp: r.taskRunTimestamp,
        state: r.taskRunState,
      })),
    }));
  try {
    localStorage.setItem(
      DIALOG_DATA_KEY,
      JSON.stringify({
        tasks: tasksSummary,
        email: options.userEmail,
        companyId: options.userCompanyId || "",
        activeBrandId: resolveDialogBrandId(options),
        existingSlideInfo,
        slideStreamId,
        initialPlan: options.initialPlan
          ? stripDeckPlanTransientContext(options.initialPlan)
          : null,
        openOnReview: Boolean(options.initialPlan),
      })
    );
  } catch {
    // localStorage may be unavailable; fall back to URL params below
  }
}

function getDialogUrl(options: DeckCreatorDialogOptions, factset: boolean = false): string {
  const baseUrl = window.location.origin;
  const emailParam = encodeURIComponent(options.userEmail);
  // Office `displayDialogAsync` dialogs run in an isolated WebView and do
  // NOT share localStorage with the parent. FactSet `window.open` popups
  // often don't share it either (WebView2 storage partition). Small values
  // can ride on the URL; the deck plan is delivered via postMessage /
  // messageChild on the dialog-ready handshake.
  const brandId = resolveDialogBrandId(options);
  const brandParam = brandId ? `&brand=${encodeURIComponent(brandId)}` : "";
  const companyIdParam = options.userCompanyId
    ? `&companyId=${encodeURIComponent(options.userCompanyId)}`
    : "";
  const base = `${baseUrl}/dialogs/deck-creator.html?email=${emailParam}${brandParam}${companyIdParam}`;
  return factset ? `${base}&factset=1` : base;
}

let activeDialog: Office.Dialog | null = null;
let activePopupWindow: Window | null = null;
let activeSlideStreamId = 0;
let activeDialogPlan: DeckPlan | null = null;
interface InflightOpenSession {
  result: Promise<DeckCreatorDialogResult>;
  opened: Promise<void>;
  markOpened: () => void;
  openedSettled: boolean;
  cancel: () => void;
  cancelAction: () => void;
  cancelRequested: boolean;
}
let inflightOpen: InflightOpenSession | null = null;
let latestSlideStreamPayload:
  | {
      streamId: number;
      slides: ExistingSlideInfo[];
      done: boolean;
      error: boolean;
      totalSlides?: number;
    }
  | null = null;

type SlideStreamSnapshot = NonNullable<typeof latestSlideStreamPayload>;

/** Parent-to-dialog retries: isolated WebViews drop messages sent before the child listener exists. */
const PLAN_PUSH_RETRY_DELAYS_MS = [0, 50, 200, 600] as const;

export function commandsForDialogReadySync(args: {
  plan: DeckPlan | null;
  slideStream: SlideStreamSnapshot | null;
  requestedStreamId?: number;
}): DeckCreatorDialogCommandPayload[] {
  const commands: DeckCreatorDialogCommandPayload[] = [];
  if (args.plan) {
    commands.push({
      type: "planUpdate",
      plan: stripDeckPlanTransientContext(args.plan),
    });
  }
  const slideStream = args.slideStream;
  if (
    !slideStream ||
    (typeof args.requestedStreamId === "number" &&
      args.requestedStreamId > 0 &&
      args.requestedStreamId !== slideStream.streamId)
  ) {
    return commands;
  }
  commands.push({
    type: "slideStreamUpdate",
    streamId: slideStream.streamId,
    slides: slideStream.slides,
    done: slideStream.done,
    error: slideStream.error,
    totalSlides: slideStream.totalSlides,
  });
  return commands;
}

function rememberDialogPlan(plan: DeckPlan | null | undefined): void {
  activeDialogPlan = plan ? stripDeckPlanTransientContext(plan) : null;
}

/** Latest Review plan, including updates queued before the dialog window exists. */
export function getRememberedDeckCreatorPlan(): DeckPlan | null {
  return activeDialogPlan;
}

function isInFlightOpenStillLive(): boolean {
  if (!inflightOpen) return false;
  return isDeckCreatorDialogOpen() || !inflightOpen.openedSettled;
}

function isExactInFlightOwner(
  session: InflightOpenSession | null
): session is InflightOpenSession {
  return Boolean(session && inflightOpen === session && !session.cancelRequested);
}

function shouldRememberPlanWithoutOpenWindow(): boolean {
  // Remember while idle (App presentation-ref wait / queued update) or while
  // the popup is still being created. Do not resurrect a plan after Review
  // has already opened and then closed.
  return !inflightOpen || !inflightOpen.openedSettled;
}

/**
 * Plan that should seed a newly opened Review window. Prefer a plan remembered
 * by present/update while the presentation-ref wait was in flight over the
 * caller's possibly-stale `initialPlan` closure. Once the previous Review has
 * opened and closed, prefer the new caller plan so a zombie remember cannot
 * seed the next window.
 */
export function resolveDeckCreatorOpenPlan(initialPlan?: DeckPlan): DeckPlan | undefined {
  if (isInFlightOpenStillLive() || !inflightOpen) {
    return activeDialogPlan ?? initialPlan;
  }
  return initialPlan ?? activeDialogPlan;
}

function seededDialogOptions(options: DeckCreatorDialogOptions): DeckCreatorDialogOptions {
  const initialPlan = resolveDeckCreatorOpenPlan(options.initialPlan);
  return initialPlan === options.initialPlan ? options : { ...options, initialPlan };
}

function requestedStreamIdFromSync(
  payload: SlideStreamSyncRequestPayload
): number | undefined {
  return typeof payload.streamId === "number" && payload.streamId > 0
    ? payload.streamId
    : undefined;
}

function isDeckCreatorCommandEnvelope(
  value: unknown
): value is DeckCreatorCommandEnvelope {
  return (
    !!value &&
    typeof value === "object" &&
    (value as { kind?: unknown }).kind === DIALOG_COMMAND_MESSAGE_KIND &&
    !!(value as { payload?: unknown }).payload &&
    typeof (value as { payload: unknown }).payload === "object"
  );
}

function schedulePlanPushRetries(shouldSend: () => boolean, send: () => void): number[] {
  return PLAN_PUSH_RETRY_DELAYS_MS.map((delay) =>
    window.setTimeout(() => {
      if (!shouldSend()) return;
      send();
    }, delay)
  );
}

function clearScheduledTimeouts(ids: number[]): void {
  for (const id of ids) {
    window.clearTimeout(id);
  }
}

function writeSlideStream(
  streamId: number,
  slides: ExistingSlideInfo[],
  done: boolean,
  options?: { error?: boolean; totalSlides?: number }
): void {
  const previousSlides =
    latestSlideStreamPayload?.streamId === streamId ? latestSlideStreamPayload.slides : [];
  const newSlides = slides.slice(previousSlides.length);
  const payload = {
    streamId,
    slides,
    done,
    error: Boolean(options?.error),
    totalSlides: typeof options?.totalSlides === "number" ? options.totalSlides : undefined,
    updatedAt: Date.now(),
  };
  latestSlideStreamPayload = {
    streamId,
    slides: [...slides],
    done,
    error: Boolean(options?.error),
    totalSlides: typeof options?.totalSlides === "number" ? options.totalSlides : undefined,
  };
  try {
    localStorage.setItem(
      DIALOG_SLIDES_KEY,
      JSON.stringify(payload)
    );
  } catch {
    // Non-critical: dialog can still work without previews.
  }
  if (activePopupWindow) {
    sendCommandToPopup(activePopupWindow, {
      type: "slideStreamUpdate",
      streamId,
      slides,
      done,
      error: Boolean(options?.error),
      totalSlides: typeof options?.totalSlides === "number" ? options.totalSlides : undefined,
    });
  }
  if (activeDialog) {
    if (newSlides.length > 0) {
      for (const slide of newSlides) {
        sendCommandToOfficeDialog(activeDialog, {
          type: "slideStreamUpdate",
          streamId,
          slide,
          done: false,
          error: false,
          totalSlides: typeof options?.totalSlides === "number" ? options.totalSlides : undefined,
        });
      }
    }
    if (done || options?.error || newSlides.length === 0) {
      sendCommandToOfficeDialog(activeDialog, {
        type: "slideStreamUpdate",
        streamId,
        slides: [],
        done,
        error: Boolean(options?.error),
        totalSlides: typeof options?.totalSlides === "number" ? options.totalSlides : undefined,
      });
    }
  }
}

function invalidateOwnedSlideStream(streamId: number): void {
  if (activeSlideStreamId === streamId) {
    activeSlideStreamId += 1;
  }
}

async function loadSlidesInBackground(streamId: number): Promise<void> {
  const slideLog = (message: string) => console.log(`[DeckDialogSlides][stream:${streamId}] ${message}`);
  try {
    const collected: ExistingSlideInfo[] = [];
    const result = await streamSlideThumbnails(
      ({ index, slideNumber, shapeCount, thumbnail }) => {
        if ((!activeDialog && !activePopupWindow) || streamId !== activeSlideStreamId) return;
        collected.push({
          index,
          slideNumber,
          ...(typeof shapeCount === "number" ? { shapeCount } : {}),
          thumbnail,
        });
        writeSlideStream(streamId, collected, false);
      },
      { height: CURRENT_PRESENTATION_PREVIEW_HEIGHT, log: slideLog }
    );
    if ((!activeDialog && !activePopupWindow) || streamId !== activeSlideStreamId) return;
    const placeholderSlides =
      collected.length === 0 && result.totalSlides > 0
        ? buildPlaceholderSlides(result.totalSlides)
        : collected;
    writeSlideStream(streamId, placeholderSlides, true, {
      error: false,
      totalSlides: result.totalSlides,
    });
  } catch {
    if ((!activeDialog && !activePopupWindow) || streamId !== activeSlideStreamId) return;
    writeSlideStream(streamId, [], true, {
      error: true,
    });
  }
}

// FactSet popup path (`window.open` + postMessage/localStorage fallback).

function isDeckCreatorPostMessage(
  value: unknown
): value is { kind: typeof DIALOG_POST_MESSAGE_KIND; payload: DeckCreatorDialogResult } {
  return (
    !!value &&
    typeof value === "object" &&
    (value as { kind?: unknown }).kind === DIALOG_POST_MESSAGE_KIND &&
    !!(value as { payload?: unknown }).payload &&
    typeof (value as { payload: unknown }).payload === "object"
  );
}

function isDeckCreatorPlanPatchMessage(
  value: unknown
): value is { kind: typeof DIALOG_PLAN_PATCH_KIND; payload: { plan: DeckPlan } } {
  return (
    !!value &&
    typeof value === "object" &&
    (value as { kind?: unknown }).kind === DIALOG_PLAN_PATCH_KIND &&
    !!(value as { payload?: { plan?: unknown } }).payload?.plan
  );
}

export function isDeckCreatorDialogOpen(): boolean {
  if (activePopupWindow) {
    try {
      return !activePopupWindow.closed;
    } catch {
      return false;
    }
  }
  return Boolean(activeDialog);
}

/** True while Review is opening or the window is still open. False after close. */
export function isDeckCreatorReviewSessionOpen(): boolean {
  return isDeckCreatorDialogOpen() || isInFlightOpenStillLive();
}

export function cancelDeckCreatorDialog(): void {
  rememberDialogPlan(null);
  latestSlideStreamPayload = null;
  activeSlideStreamId += 1;
  inflightOpen?.cancel();

  const popup = activePopupWindow;
  activePopupWindow = null;
  if (popup) {
    try {
      if (!popup.closed) {
        sendCommandToPopup(popup, { type: "close" });
        popup.close();
      }
    } catch {
      // Popup may already be closed.
    }
  }

  const dialog = activeDialog;
  activeDialog = null;
  if (dialog) {
    sendCommandToOfficeDialog(dialog, { type: "close" });
    try {
      dialog.close();
    } catch {
      // Dialog may already be closed.
    }
  }
  clearStagedResult();
}

export function pushPlanToDeckCreatorDialog(plan: DeckPlan): boolean {
  const safePlan = stripDeckPlanTransientContext(plan);
  if (isDeckCreatorDialogOpen() || shouldRememberPlanWithoutOpenWindow()) {
    rememberDialogPlan(safePlan);
  }
  if (activePopupWindow) {
    try {
      if (!activePopupWindow.closed) {
        sendCommandToPopup(activePopupWindow, {
          type: "planUpdate",
          plan: safePlan,
        });
        return true;
      }
    } catch {
      return false;
    }
  }
  if (activeDialog) {
    sendCommandToOfficeDialog(activeDialog, {
      type: "planUpdate",
      plan: safePlan,
    });
    return true;
  }
  return false;
}

function attachToInFlightOpen(options: DeckCreatorDialogOptions): Promise<DeckCreatorDialogResult> {
  const session = inflightOpen;
  if (!session) {
    return Promise.resolve({ cancelled: true });
  }
  // Do not push options.initialPlan here: it is often the closed-over original
  // present() argument and would overwrite a newer queued update.
  if (activeDialogPlan && isDeckCreatorDialogOpen()) {
    pushPlanToDeckCreatorDialog(activeDialogPlan);
  }
  return session.opened.then(() => {
    options.onOpened?.();
    return session.result;
  });
}

function beginInFlightOpen(
  start: (markOpened: () => void) => Promise<DeckCreatorDialogResult>
): Promise<DeckCreatorDialogResult> {
  let resolveCancellation: (result: DeckCreatorDialogResult) => void = () => {};
  const cancellationResult = new Promise<DeckCreatorDialogResult>((resolve) => {
    resolveCancellation = resolve;
  });
  const session: InflightOpenSession = {
    result: Promise.resolve({ cancelled: true }),
    opened: Promise.resolve(),
    markOpened: () => {},
    openedSettled: false,
    cancel: () => {
      session.cancelRequested = true;
      session.cancelAction();
      session.markOpened();
      resolveCancellation({ cancelled: true });
    },
    cancelAction: () => {},
    cancelRequested: false,
  };
  session.opened = new Promise<void>((resolve) => {
    session.markOpened = () => {
      if (session.openedSettled) return;
      session.openedSettled = true;
      resolve();
    };
  });
  inflightOpen = session;
  let startedResult: Promise<DeckCreatorDialogResult>;
  try {
    startedResult = start(session.markOpened);
  } catch (error) {
    startedResult = Promise.reject(error);
  }
  void startedResult.finally(() => {
    if (inflightOpen === session) {
      inflightOpen = null;
    }
    session.markOpened();
  }).catch(() => {
    // The returned race observes this rejection unless cancellation won first.
  });
  const result = Promise.race([startedResult, cancellationResult]);
  session.result = result;
  return result;
}

function setInFlightCancelAction(
  session: InflightOpenSession | null,
  action: () => void
): void {
  if (!session) return;
  session.cancelAction = action;
  if (session.cancelRequested) {
    action();
  }
}

function readStagedResultFromStorage():
  | DeckCreatorDialogResult
  | null {
  try {
    const raw = localStorage.getItem(DIALOG_RESULT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isDeckCreatorPostMessage(parsed)) return null;
    return parsed.payload as DeckCreatorDialogResult;
  } catch {
    return null;
  }
}

function clearStagedResult(): void {
  try {
    localStorage.removeItem(DIALOG_RESULT_STORAGE_KEY);
  } catch {
    // Non-critical; stale entry will be overwritten on next open.
  }
}

function sendCommandToPopup(popup: Window, command: DeckCreatorCommandEnvelope["payload"]): void {
  try {
    const envelope: DeckCreatorCommandEnvelope = {
      kind: DIALOG_COMMAND_MESSAGE_KIND,
      payload: command,
    };
    popup.postMessage(envelope, window.location.origin);
  } catch {
    // ignore
  }
}

function sendCommandToOfficeDialog(
  dialog: Office.Dialog,
  command: DeckCreatorCommandEnvelope["payload"]
): void {
  try {
    const envelope: DeckCreatorCommandEnvelope = {
      kind: DIALOG_COMMAND_MESSAGE_KIND,
      payload: command,
    };
    const dialogWithChildMessaging = dialog as Office.Dialog & {
      messageChild?: (message: string) => void;
    };
    dialogWithChildMessaging.messageChild?.(JSON.stringify(envelope));
  } catch {
    // Some Office hosts do not support parent-to-dialog messaging.
  }
}

function replayDialogStateToPopup(popup: Window, requestedStreamId?: number): void {
  for (const command of commandsForDialogReadySync({
    plan: activeDialogPlan,
    slideStream: latestSlideStreamPayload,
    requestedStreamId,
  })) {
    sendCommandToPopup(popup, command);
  }
}

function replayDialogStateToOfficeDialog(
  dialog: Office.Dialog,
  requestedStreamId?: number
): void {
  const commands = commandsForDialogReadySync({
    plan: activeDialogPlan,
    slideStream: latestSlideStreamPayload,
    requestedStreamId,
  });
  for (const command of commands) {
    if (command.type !== "slideStreamUpdate") {
      sendCommandToOfficeDialog(dialog, command);
      continue;
    }
    const slides = Array.isArray(command.slides) ? command.slides : [];
    for (const slide of slides) {
      sendCommandToOfficeDialog(dialog, {
        type: "slideStreamUpdate",
        streamId: command.streamId,
        slide,
        done: false,
        error: false,
        totalSlides: command.totalSlides,
      });
    }
    sendCommandToOfficeDialog(dialog, {
      type: "slideStreamUpdate",
      streamId: command.streamId,
      slides: [],
      done: command.done,
      error: command.error,
      totalSlides: command.totalSlides,
    });
  }
}

function normalizeDialogResult(
  message: DeckCreatorDialogResult
): DeckCreatorDialogResult {
  if (message.cancelled) return { cancelled: true };
  if (message.config) {
    const config: DeckConfig = {
      topic: message.config.topic || "",
      slideCount: message.config.slideCount || 5,
      additionalContext: message.config.additionalContext || "",
      selectedTaskId: message.config.selectedTaskId || "",
      selectedTaskRunId: message.config.selectedTaskRunId || "",
      styleConfig: {
        source: (message.config.styleConfig?.source as StyleConfig["source"]) || "existing",
        pdfStyleImage: message.config.styleConfig?.pdfStyleImage,
        description: message.config.styleConfig?.description,
      },
      includeTitleSlide: message.config.includeTitleSlide ?? true,
      includeSummarySlide: message.config.includeSummarySlide ?? true,
      insertAfterCurrent: message.config.insertAfterCurrent ?? true,
      brandId: message.config.brandId ?? null,
    };
    return {
      config,
      plan: message.plan ? stripDeckPlanTransientContext(message.plan) : undefined,
    };
  }
  if (message.error) return { error: message.error };
  return { cancelled: true };
}

function openDeckCreatorDialogViaFactSet(
  options: DeckCreatorDialogOptions,
  markOpened: () => void
): Promise<DeckCreatorDialogResult> {
  const ownerSession = inflightOpen;
  if (activePopupWindow) {
    try {
      if (!activePopupWindow.closed) {
        sendCommandToPopup(activePopupWindow, { type: "close" });
        activePopupWindow.close();
      }
    } catch {
      // ignore
    }
    activePopupWindow = null;
    activeSlideStreamId += 1;
  }

  const seeded = seededDialogOptions(options);
  return new Promise((resolve, reject) => {
    const streamId = ++activeSlideStreamId;
    latestSlideStreamPayload = null;
    rememberDialogPlan(seeded.initialPlan);
    stageDialogData(seeded, [], streamId);
    writeSlideStream(streamId, [], false);
    clearStagedResult();

    const dialogUrl = getDialogUrl(seeded, true);
    const popupWindow = buildCenteredDialogPopupWindow();
    // Do not use noopener; popup needs window.opener.
    const popup = window.open(
      dialogUrl,
      "finster_deck_creator",
      popupWindow.features
    );

    if (!popup) {
      rememberDialogPlan(null);
      reject(new Error("Popup blocked. Please allow popups for this site and try again."));
      return;
    }

    applyDialogPopupWindowGeometry(popup, popupWindow);
    focusDialogPopupWindow(popup);

    activePopupWindow = popup;
    markOpened();
    options.onOpened?.();
    let settled = false;
    let confirmationPending = false;
    void loadSlidesInBackground(streamId);
    const planPushRetryIds = schedulePlanPushRetries(
      () => !settled && activePopupWindow === popup && !popup.closed,
      () => replayDialogStateToPopup(popup)
    );

    const teardown = (): void => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("storage", onStorage);
      window.clearInterval(closeWatcher);
      clearScheduledTimeouts(planPushRetryIds);
      clearStagedResult();
      if (activePopupWindow === popup) {
        activePopupWindow = null;
        rememberDialogPlan(null);
      }
      invalidateOwnedSlideStream(streamId);
      try {
        if (!popup.closed) {
          sendCommandToPopup(popup, { type: "close" });
          popup.close();
        }
      } catch {
        // popup may already be closed
      }
    };

    const settle = (response: DeckCreatorDialogResult): void => {
      if (settled) return;
      settled = true;
      teardown();
      resolve(response);
    };

    const fail = (error: Error): void => {
      if (settled) return;
      settled = true;
      teardown();
      reject(error);
    };

    const settleWithConfirmationBarrier = (response: DeckCreatorDialogResult): void => {
      let normalized: DeckCreatorDialogResult;
      try {
        normalized = normalizeDialogResult(response);
      } catch (error) {
        fail(error instanceof Error ? error : new Error("Failed to parse dialog response"));
        return;
      }
      if (!normalized.config || !options.beforeConfirm) {
        settle(normalized);
        return;
      }
      if (confirmationPending) return;
      confirmationPending = true;
      void options
        .beforeConfirm()
        .then(() => settle(normalized))
        .catch((error) =>
          fail(error instanceof Error ? error : new Error("Failed to finish the Review update"))
        );
    };

    setInFlightCancelAction(ownerSession, () => settle({ cancelled: true }));

    const settleFromStagedResult = (): boolean => {
      const staged = readStagedResultFromStorage();
      if (!staged) return false;
      settleWithConfirmationBarrier(staged);
      return true;
    };

    function onMessage(event: MessageEvent): void {
      if (event.source !== popup) return;
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      const commandData = data as Partial<DeckCreatorCommandEnvelope> | null;
      if (
        isDeckCreatorCommandEnvelope(commandData) &&
        commandData.payload.type === "requestSlideStreamSync"
      ) {
        replayDialogStateToPopup(popup, requestedStreamIdFromSync(commandData.payload));
        return;
      }
      if (isDeckCreatorPlanPatchMessage(data)) {
        const safePlan = stripDeckPlanTransientContext(data.payload.plan);
        rememberDialogPlan(safePlan);
        options.onPlanPatch?.(safePlan);
        return;
      }
      if (!isDeckCreatorPostMessage(data)) return;
      settleWithConfirmationBarrier(data.payload as DeckCreatorDialogResult);
    }

    // Fallback channel for cases where postMessage is dropped.
    function onStorage(event: StorageEvent): void {
      if (event.key !== DIALOG_RESULT_STORAGE_KEY) return;
      if (!event.newValue) return;
      settleFromStagedResult();
    }

    window.addEventListener("message", onMessage);
    window.addEventListener("storage", onStorage);

    // On close, do one final localStorage drain before treating it as cancel.
    const closeWatcher = window.setInterval(() => {
      if (!popup.closed) return;
      if (settleFromStagedResult()) return;
      settle({ cancelled: true });
    }, 500);
  });
}

export async function openDeckCreatorDialog(options: DeckCreatorDialogOptions): Promise<DeckCreatorDialogResult> {
  if (isInFlightOpenStillLive()) {
    return attachToInFlightOpen(options);
  }
  if (inflightOpen) {
    // Previous Review already opened and then closed. Drop that session's plan
    // and finish its teardown before this window starts a new slide stream,
    // otherwise the prior close-watcher would bump activeSlideStreamId and
    // starve the new carousel of current-deck thumbnails.
    rememberDialogPlan(null);
    inflightOpen.cancel();
  }

  return beginInFlightOpen((markOpened) => {
    if (shouldUseFactSetBridge()) {
      return openDeckCreatorDialogViaFactSet(options, markOpened);
    }
    if (isOfficeJsBypassed()) {
      console.log("[OfficeJS NOP] openDeckCreatorDialog bypassed");
      markOpened();
      options.onOpened?.();
      return Promise.resolve({ cancelled: true });
    }
    return openDeckCreatorDialogViaOffice(options, markOpened);
  });
}

function openDeckCreatorDialogViaOffice(
  options: DeckCreatorDialogOptions,
  markOpened: () => void
): Promise<DeckCreatorDialogResult> {
  const ownerSession = inflightOpen;
  if (activeDialog) {
    try {
      activeDialog.close();
    } catch {
      // Ignore close errors
    }
    activeDialog = null;
    activeSlideStreamId += 1;
  }

  const seeded = seededDialogOptions(options);
  return new Promise((resolve, reject) => {
    const streamId = ++activeSlideStreamId;
    rememberDialogPlan(seeded.initialPlan);
    stageDialogData(seeded, [], streamId);
    writeSlideStream(streamId, [], false);
    const dialogUrl = getDialogUrl(seeded);

    const isWeb = Office.context.platform === Office.PlatformType.OfficeOnline;

    const width = 76;
    const height = 82;

    const abandonUnownedCallback = (dialog?: Office.Dialog): void => {
      if (dialog) {
        try {
          dialog.close();
        } catch {
          // Dialog may already be closed.
        }
      }
      invalidateOwnedSlideStream(streamId);
      resolve({ cancelled: true });
    };

    // Cancellation can happen while Office is still creating the dialog. Set
    // the action before displayDialogAsync so the operation itself settles
    // immediately; a late Office callback is rejected by the exact-owner
    // check below and closes only the dialog it returned.
    setInFlightCancelAction(ownerSession, () => {
      invalidateOwnedSlideStream(streamId);
      resolve({ cancelled: true });
    });

    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height, width, promptBeforeOpen: false, displayInIframe: isWeb },
      (result) => {
        if (!isExactInFlightOwner(ownerSession)) {
          abandonUnownedCallback(
            result.status === Office.AsyncResultStatus.Failed ? undefined : result.value
          );
          return;
        }

        if (result.status === Office.AsyncResultStatus.Failed) {
          rememberDialogPlan(null);
          invalidateOwnedSlideStream(streamId);
          reject(new Error(result.error.message));
          return;
        }

        const dialog = result.value;
        activeDialog = dialog;
        const ownsDialog = (): boolean =>
          isExactInFlightOwner(ownerSession) && activeDialog === dialog;
        if (!ownsDialog()) {
          abandonUnownedCallback(dialog);
          return;
        }

        markOpened();
        if (!ownsDialog()) {
          abandonUnownedCallback(dialog);
          return;
        }
        options.onOpened?.();
        if (!ownsDialog()) {
          abandonUnownedCallback(dialog);
          return;
        }

        let settled = false;
        let confirmationPending = false;
        let planPushRetryIds: number[] = [];

        const settle = (response: DeckCreatorDialogResult): void => {
          if (settled) return;
          settled = true;
          clearScheduledTimeouts(planPushRetryIds);
          if (activeDialog === dialog) {
            activeDialog = null;
            rememberDialogPlan(null);
          }
          invalidateOwnedSlideStream(streamId);
          try {
            dialog.close();
          } catch {
            // Dialog may already be closed
          }
          resolve(response);
        };

        const fail = (error: Error): void => {
          if (settled) return;
          settled = true;
          clearScheduledTimeouts(planPushRetryIds);
          if (activeDialog === dialog) {
            activeDialog = null;
            rememberDialogPlan(null);
          }
          invalidateOwnedSlideStream(streamId);
          try {
            dialog.close();
          } catch {
            // Dialog may already be closed
          }
          reject(error);
        };

        const settleWithConfirmationBarrier = (response: DeckCreatorDialogResult): void => {
          if (!response.config || !options.beforeConfirm) {
            settle(response);
            return;
          }
          if (confirmationPending) return;
          confirmationPending = true;
          void options
            .beforeConfirm()
            .then(() => settle(response))
            .catch((error) =>
              fail(error instanceof Error ? error : new Error("Failed to finish the Review update"))
            );
        };

        setInFlightCancelAction(ownerSession, () => settle({ cancelled: true }));
        if (!ownsDialog()) {
          settle({ cancelled: true });
          return;
        }

        void loadSlidesInBackground(streamId);
        planPushRetryIds = schedulePlanPushRetries(
          () => !settled && ownsDialog(),
          () => replayDialogStateToOfficeDialog(dialog)
        );

        if (!ownsDialog()) {
          settle({ cancelled: true });
          return;
        }
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          if (!ownsDialog()) return;
          try {
            const parsed = JSON.parse((arg as { message: string }).message) as unknown;
            if (isDeckCreatorCommandEnvelope(parsed) && parsed.payload.type === "requestSlideStreamSync") {
              replayDialogStateToOfficeDialog(
                dialog,
                requestedStreamIdFromSync(parsed.payload)
              );
              return;
            }
            if (isDeckCreatorPlanPatchMessage(parsed)) {
              const safePlan = stripDeckPlanTransientContext(parsed.payload.plan);
              rememberDialogPlan(safePlan);
              options.onPlanPatch?.(safePlan);
              return;
            }
            const message = parsed as DeckCreatorDialogResult & {
              plan?: DeckPlan;
            };

            if (message.cancelled) {
              settle({ cancelled: true });
            } else if (message.config) {
              const parsedSlideCount =
                typeof message.config.slideCount === "number" &&
                Number.isInteger(message.config.slideCount) &&
                message.config.slideCount >= 1 &&
                message.config.slideCount <= 20
                  ? message.config.slideCount
                  : undefined;
              const config: DeckConfig = {
                topic: message.config.topic || "",
                slideCount: parsedSlideCount,
                additionalContext: message.config.additionalContext || "",
                selectedTaskId: message.config.selectedTaskId || "",
                selectedTaskRunId: message.config.selectedTaskRunId || "",
                styleConfig: {
                  source: (message.config.styleConfig?.source as StyleConfig["source"]) || "existing",
                  pdfStyleImage: message.config.styleConfig?.pdfStyleImage,
                  description: message.config.styleConfig?.description,
                },
                includeTitleSlide: message.config.includeTitleSlide ?? false,
                includeSummarySlide: message.config.includeSummarySlide ?? false,
                insertAfterCurrent: message.config.insertAfterCurrent ?? true,
                brandId: message.config.brandId ?? null,
              };
              settleWithConfirmationBarrier({
                config,
                plan: message.plan ? stripDeckPlanTransientContext(message.plan) : undefined,
              });
            } else if (message.error) {
              settle({ error: message.error });
            } else {
              settle({ cancelled: true });
            }
          } catch (e) {
            fail(new Error("Failed to parse dialog response"));
          }
        });

        if (!ownsDialog()) {
          settle({ cancelled: true });
          return;
        }
        dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
          if (!ownsDialog()) return;
          const eventArg = arg as { error: number };
          // 12006 = Dialog closed by user
          if (eventArg.error === 12006) {
            settle({ cancelled: true });
          } else {
            settle({ error: `Dialog error: ${eventArg.error}` });
          }
        });
      }
    );
  });
}

export function resetDeckCreatorDialogStateForTests(): void {
  activeDialog = null;
  activePopupWindow = null;
  activeDialogPlan = null;
  inflightOpen = null;
  latestSlideStreamPayload = null;
  activeSlideStreamId = 0;
}
